'use client'

/**
 * Purpose: Sticky "recommended for you" header for the products page — title
 * and category chips that collapse to just the chips/Filters row once pinned
 * under the (possibly hidden) navbar, on both mobile and desktop.
 * Inputs: title/subtitle copy, visible categories, the active scroll-spy
 * category, filters label/handler, and category-select handler.
 * Outputs: renders the header; `data-navbar-hidden`/`data-stuck` attributes
 * drive productsStickyHeader.css's collapse and are read directly by e2e
 * tests (tests/e2e/products.spec.ts).
 */

import { useEffect, useRef } from 'react'
import HoverGradientButton from '@components/buttons/HoverGradientButton/HoverGradientButton'
import {
  freezeNavbarScrollDirection,
  NAVBAR_HIDE_DURATION_S,
  useNavbarScrollHidden,
} from '@/hooks/use-navbar-scroll-hidden'
import { useNavbarStickyTop } from '@/hooks/use-navbar-sticky-top'
import './productsStickyHeader.css'

/** Matches useNavbarStickyTop's settle window — freeze direction detection at
 *  least this long while sticky `top` tracks the navbar hide/show. */
const STICKY_TOP_SETTLE_MS = NAVBAR_HIDE_DURATION_S * 1000 + 120

/** Skip a no-op horizontal nudge when the chip is already centered in the row. */
const CHIP_ALIGN_EPSILON_PX = 1

/**
 * Slide `chip` to the center of `nav`'s visible range by changing only
 * `nav.scrollLeft`. Using the row's own `scrollTo` (not Element.scrollIntoView)
 * cannot move the page vertically, so the products scroll-spy stays put.
 */
export function alignChipInRow(nav: HTMLElement, chip: HTMLElement): void {
  const navRect = nav.getBoundingClientRect()
  const chipRect = chip.getBoundingClientRect()
  const navCenter = (navRect.left + navRect.right) / 2
  const chipCenter = (chipRect.left + chipRect.right) / 2
  const delta = chipCenter - navCenter
  if (Math.abs(delta) < CHIP_ALIGN_EPSILON_PX) return
  const nextLeft = nav.scrollLeft + delta
  if (typeof nav.scrollTo === 'function') {
    nav.scrollTo({ left: nextLeft, behavior: 'smooth' })
    return
  }
  nav.scrollLeft = nextLeft
}

export type ProductsStickyHeaderProps = {
  title: string
  subtitle: string
  /** Only categories that currently have visible products. */
  categories: { value: string; label: string }[]
  activeCategory: string | null
  filtersLabel: string
  jumpLabel: (label: string) => string
  onSelectCategory: (value: string) => void
  onOpenFilters: () => void
}

// `isStuck` (whether the header has actually reached its sticky position, and
// whether it is still there — see the title-collapse note below) now comes
// straight from useNavbarStickyTop, which derives it from the same per-frame
// measurement that writes the header's `top`. It used to be a second,
// independently-polled hook here; see that hook's doc comment for why that
// was fragile under fast scrolling.

export function ProductsStickyHeader({
  title,
  subtitle,
  categories,
  activeCategory,
  filtersLabel,
  jumpLabel,
  onSelectCategory,
  onOpenFilters,
}: ProductsStickyHeaderProps) {
  const navbarHidden = useNavbarScrollHidden()
  const headerRef = useRef<HTMLDivElement>(null)
  const chipsRef = useRef<HTMLElement>(null)
  const didMount = useRef(false)

  // Toggling navbarHidden animates the sticky `top`, which can nudge scrollY
  // (scroll anchoring / bottom clamping). useNavbarScrollHidden would read that
  // as a user scroll and flip navbarHidden back, yanking the header in a
  // jitter loop. Freeze direction detection — the same gate chip-jumps use —
  // until the animation settles, on both breakpoints.
  useEffect(() => {
    // First render is the resting state — nothing is animating yet.
    if (!didMount.current) {
      didMount.current = true
      return
    }
    if (typeof window === 'undefined') return

    const unfreeze = freezeNavbarScrollDirection()
    const timer = window.setTimeout(unfreeze, STICKY_TOP_SETTLE_MS)
    return () => {
      window.clearTimeout(timer)
      unfreeze()
    }
  }, [navbarHidden])

  // Bind the sticky `top` to the navbar's live visible bottom edge on both
  // breakpoints, so the header stays flush with the bar through the whole
  // hide/show — mobile's CSS collapse (gated on data-stuck below) rides on top
  // of this instead of pinning `top` separately. `isStuck` is derived from
  // that same tracked position. See use-navbar-sticky-top.
  const isStuck = useNavbarStickyTop(headerRef, navbarHidden)

  // Keep the scroll-spy's active chip inside the row as the visitor moves
  // between category sections. Horizontal only — see alignChipInRow.
  useEffect(() => {
    const nav = chipsRef.current
    if (!nav || !activeCategory) return
    const chip = document.getElementById(`products-category-chip-${activeCategory}`)
    if (!chip) return
    alignChipInRow(nav, chip)
  }, [activeCategory])

  // data-navbar-hidden/data-stuck drive productsStickyHeader.css's collapse
  // and are asserted on directly by tests/e2e/products.spec.ts.
  return (
    <div
      ref={headerRef}
      id="products-sticky-header"
      data-testid="products-sticky-header"
      data-navbar-hidden={navbarHidden ? 'true' : 'false'}
      data-stuck={isStuck ? 'true' : 'false'}
      className="products-sticky-header"
    >
      <div id="products-sticky-header-inner" className="products-sticky-header-inner">
        <div id="products-sticky-header-content" className="products-sticky-header-content">
          {/* Collapses via CSS grid-rows when data-stuck='true' — see productsStickyHeader.css. */}
          <div id="products-sticky-header-title-block" className="products-sticky-header-title-block">
            <div id="products-sticky-header-title-block-inner" className="products-sticky-header-title-block-inner">
              <h1 id="products-sticky-header-title" className="products-sticky-header-title">{title}</h1>
              <p id="products-sticky-header-subtitle" className="products-sticky-header-subtitle">{subtitle}</p>
            </div>
          </div>

          {/* Scroll-spy category jump chips — stay visible through the title collapse. */}
          <nav
            ref={chipsRef}
            id="products-sticky-header-chips"
            className="products-sticky-header-chips"
            aria-label={title}
          >
            {categories.map((cat) => {
              const isActive = activeCategory === cat.value
              return (
                <HoverGradientButton
                  key={cat.value}
                  id={`products-category-chip-${cat.value}`}
                  type="button"
                  variant={isActive ? 'filled' : 'outline'}
                  className="products-category-chip"
                  ariaCurrent={isActive || undefined}
                  ariaLabel={jumpLabel(cat.label)}
                  onClick={() => onSelectCategory(cat.value)}
                >
                  {cat.label}
                </HoverGradientButton>
              )
            })}
          </nav>

          {/* Opens the mobile filters panel; desktop filtering lives in the sidebar instead. */}
          <div id="products-sticky-header-filters" className="products-sticky-header-filters">
            <button id="products-open-filters-button" type="button" className="products-filters-link uppercase" onClick={onOpenFilters}>
              {filtersLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
